import {
  FetchResult,
  InternalRefetchQueriesInclude,
  QueryHookOptions,
} from '@apollo/client';
import { unionBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useMachineContext } from '../../pages/machine/machine-context-provider';
import { SystemUnit } from '../../scratchpad/application/shared/model/flow.types';
import { useActiveWorkspace } from '../../store/workspace/workspace.hooks';
import {
  GetPreferenceDocument,
  GetPreferenceQuery,
  GetPreferenceQueryVariables,
  Preference,
  PreferenceTargetType,
  SavePreferenceInput,
  SavePreferenceMutation,
  useGetPreferenceQuery,
  useListPreferencesQuery,
  useSavePreferenceMutation,
} from '../graphql/generated';
import { useCurrentUserId, useCurrentUserProp } from '../user.hooks';

export enum UserPreferences {
  FAVORITE_IO = 'favorite.io',
  FAVORITE_VARIABLES = 'favorite.variables',
  USER_THEME = 'user.theme',
  USER_LANGUAGE = 'user.language',
  UNIT_OF_MEASURE = 'user.unit_of_measure',
}

export enum TenantPreference {
  PARTNER = 'partner.name',
}

type SetPreferenceReturnType =
  | FetchResult<
      SavePreferenceMutation,
      Record<string, unknown>,
      Record<string, unknown>
    >
  | undefined;

type SetPreferenceType<T> = (
  preference: SetPreferenceInput<T>,
  options?: SavePreferenceOptions
) => Promise<SetPreferenceReturnType>;

type TypedPreference<ValueType = unknown> = Omit<Preference, 'value'> & {
  value: ValueType;
};

type SetPreferenceInput<T> = Pick<TypedPreference<T>, 'key' | 'value'>;
type SavePreferenceOptions = {
  refetchQueries?: InternalRefetchQueriesInclude;
};

export const useSavePreferenceLazy = (): [
  (
    preference: SavePreferenceInput,
    options?: SavePreferenceOptions
  ) => Promise<SetPreferenceReturnType>,
] => {
  const [savePreference] = useSavePreferenceMutation();

  const setPreference = useCallback(
    async (
      preference: SavePreferenceInput,
      options?: SavePreferenceOptions
    ): Promise<SetPreferenceReturnType> => {
      const { refetchQueries } = options ?? {};

      let refetch: InternalRefetchQueriesInclude = [
        {
          query: GetPreferenceDocument,
          variables: {
            input: {
              targetType: preference.targetType,
              targetId: preference.targetId,
              key: preference.key,
            },
          },
        },
      ];

      if (refetchQueries !== undefined) {
        // this will not allow multiple of the same query by the document and prefer the one passed in
        refetch = unionBy(refetchQueries, refetch, 'query');
      }

      return savePreference({
        variables: {
          input: preference,
        },
        refetchQueries: refetch,
      });
    },
    [savePreference]
  );

  return [setPreference];
};

export const useSetTenantPreferenceLazy = <T,>(): [SetPreferenceType<T>] => {
  const [savePreferenceLazy] = useSavePreferenceLazy();
  const workspace = useActiveWorkspace();
  const targetId = workspace?.tenant.id;

  const setPreference = useCallback(
    async (
      preference: SetPreferenceInput<T>,
      options?: SavePreferenceOptions
    ): Promise<SetPreferenceReturnType> => {
      if (targetId === undefined) {
        return;
      }

      return savePreferenceLazy(
        {
          ...preference,
          targetType: PreferenceTargetType.TENANT,
          targetId,
        },
        options
      );
    },
    [savePreferenceLazy, targetId]
  );

  return [setPreference];
};

export const useSetUserPreferenceLazy = <T,>(): [SetPreferenceType<T>] => {
  const [savePreferenceLazy] = useSavePreferenceLazy();
  const targetId = useCurrentUserId();

  const setPreference = useCallback(
    async (
      preference: SetPreferenceInput<T>,
      options?: SavePreferenceOptions
    ): Promise<SetPreferenceReturnType> => {
      if (targetId === undefined) {
        return;
      }

      return savePreferenceLazy(
        {
          ...preference,
          targetType: PreferenceTargetType.USER,
          targetId,
        },
        options
      );
    },
    [savePreferenceLazy, targetId]
  );

  return [setPreference];
};

export const useSetMachinePreferenceLazy = <T,>(): [SetPreferenceType<T>] => {
  const [savePreferenceLazy] = useSavePreferenceLazy();
  const { machine } = useMachineContext();

  const setPreference = useCallback(
    async (
      preference: SetPreferenceInput<T>
    ): Promise<SetPreferenceReturnType> => {
      if (!machine.id) {
        return;
      }

      return savePreferenceLazy({
        ...preference,
        targetType: PreferenceTargetType.MACHINE,
        targetId: machine.id,
      });
    },
    [savePreferenceLazy, machine]
  );

  return [setPreference];
};

type GetPreferenceOptions = Omit<
  QueryHookOptions<GetPreferenceQuery, GetPreferenceQueryVariables>,
  'variables'
>;

const useGetPreference = <T,>(
  targetId: string | undefined,
  targetType: PreferenceTargetType,
  path: string,
  queryOptions?: GetPreferenceOptions
): Preference | undefined => {
  const { data: getPreferenceData } = useGetPreferenceQuery({
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        targetType,
        targetId: String(targetId),
        key: path,
      },
    },
    skip: targetId === undefined,
    ...queryOptions,
  });

  return (getPreferenceData?.getPreference as TypedPreference<T>) ?? undefined;
};

interface PreferenceWithLoading<T> {
  preference: TypedPreference<T> | undefined;
  loading: boolean;
}

export const useGetPreferenceWithLoading = <T,>(
  targetId: string | undefined,
  targetType: PreferenceTargetType,
  path: string,
  queryOptions?: GetPreferenceOptions
): PreferenceWithLoading<T> => {
  const { data: getPreferenceData, loading } = useGetPreferenceQuery({
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        targetType,
        targetId: String(targetId),
        key: path,
      },
    },
    skip: targetId === undefined,
    ...queryOptions,
  });

  return {
    preference:
      (getPreferenceData?.getPreference as TypedPreference<T>) ?? undefined,
    loading,
  };
};

export const useGetUserPreference = <T,>(
  path: string,
  queryOptions?: GetPreferenceOptions
): TypedPreference<T> | undefined => {
  const userId = useCurrentUserId();

  return useGetPreference<T>(userId, PreferenceTargetType.USER, path, {
    // default to cache only for preferences since we query for these
    // into our hydrate workspace call
    fetchPolicy: 'cache-only',
    ...queryOptions,
  });
};

export const useGetUserPreferenceWithLoading = <T,>(
  path: string,
  queryOptions?: GetPreferenceOptions
): PreferenceWithLoading<T> => {
  const userId = useCurrentUserId();

  return useGetPreferenceWithLoading<T>(
    userId,
    PreferenceTargetType.USER,
    path,
    {
      // default to cache only for preferences since we query for these
      // into our hydrate workspace call
      fetchPolicy: 'cache-only',
      ...queryOptions,
      skip: userId === undefined,
    }
  );
};

export const useGetTenantPreference = <T,>(
  path: string
): TypedPreference<T> | undefined => {
  const tenantId = useCurrentUserProp<string>('tenantId');
  return useGetPreference<T>(tenantId, PreferenceTargetType.TENANT, path, {
    // default to cache only for preferences since we query for these
    // into our hydrate workspace call
    fetchPolicy: 'cache-only',
  });
};

export const useGetMachinePreference = <T,>(
  path: string
): TypedPreference<T> | undefined => {
  const { machine } = useMachineContext();
  return useGetPreference<T>(machine.id, PreferenceTargetType.MACHINE, path);
};

const keys = ['theme', 'language', 'unit_of_measure'] as const;
type UserPreferenceKey = (typeof keys)[number];
type UserPreferenceByKeyResult = Record<UserPreferenceKey, unknown>;

export function isOfTypeUserPreferenceKey(
  key: string
): key is UserPreferenceKey {
  return (keys as readonly string[]).includes(key);
}

export const useUserPreferencesByKey = (): UserPreferenceByKeyResult => {
  const userId = useCurrentUserProp<string>('id');
  const { data: userPreferences } = useListPreferencesQuery({
    fetchPolicy: 'cache-only',
    variables: {
      input: {
        targetId: userId as string,
        targetType: PreferenceTargetType.USER,
      },
    },
    skip: userId === undefined,
  });

  const userPreferenceList = useMemo(
    () => userPreferences?.listPreferences.items ?? [],
    [userPreferences?.listPreferences.items]
  );

  const preferences = useMemo(() => {
    const prefs = userPreferenceList.reduce((mapped, preference) => {
      const key = preference.key.split('.')[1];
      if (isOfTypeUserPreferenceKey(key)) {
        mapped[key] = preference.value;
      }

      return mapped;
    }, {} as UserPreferenceByKeyResult);

    // providing defaults
    prefs.language = prefs.language ?? 'system';
    prefs.theme = prefs.theme ?? 'system';
    prefs.unit_of_measure = prefs.unit_of_measure ?? SystemUnit.IMPERIAL;

    return prefs;
  }, [userPreferenceList]);

  return preferences;
};
